import ListSearchBar from "../components/ListSearchBar";
import React, { useState, useEffect, useRef } from "react";
import { Dropdown, Table } from "react-bootstrap";
import { Link, NavLink, useParams } from "react-router-dom";
import ReactPaginate from "react-paginate";
import { useTranslate } from "../hooks/LanguageProvider";
import { formatDateInServerUtc, formatNotificationMessage, parseServerUtcDate, toTimestamp } from "../utilities/functions";
import { getReportNotificationLabel, getReportNotificationPath } from "../utilities/reportNotificationLabels";
import { PropagateLoader } from "react-spinners";
import "../assets/css/notifications.css";

const getTodayDateInputValue = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

interface NotificationsProps {
  id_notification: number;
  id_groupe_alarme: number;
  name_groupe_alarme: string;
  name_alarme: string;
  id_user: number;
  type: number | string;
  webhook_state?: 'pending' | 'retry' | 'review' | 'delivered';
  webhook_attempts?: number;
  webhook_http_status?: number;
  webhook_error?: string;
  webhook_delivered_at?: string;
  webhook_next_attempt_at?: string;
  trip_created_at?: string | null;
  trip_event_status?: 'arrived' | 'delayed' | null;
  message: string;
  severity: number;
  timestamp: string;
  state: number;
  attached: number;
  date_start: string;
  immatriculation_vehicule: string;
  report_id?: string | number | null;
  report_type?: string | number | null;
  report_turn?: string | number | null;
  report_device_id?: string | number | null;
}

export function Notifications() {
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const { id_notification } = useParams<{ id_notification?: string }>();

  const { translate } = useTranslate();
  const getNotificationGroupLabel = (notification: NotificationsProps) =>
    getReportNotificationLabel(notification.type.toString(), translate, notification.report_type)
      ? translate("Report")
      : notification.type === "security_alert" || notification.type === "security_report"
        ? translate("Security center")
        : notification.type === "trip_detected"
          ? "Mission"
          : notification.name_groupe_alarme;

  const getNotificationNameLabel = (notification: NotificationsProps) =>
    getReportNotificationLabel(notification.type.toString(), translate, notification.report_type)
      || (notification.type === "security_alert"
        ? translate("Security alert")
        : notification.type === "security_report"
          ? translate("Security report")
          : notification.type === "trip_detected"
            ? "Trip détecté"
            : notification.name_alarme);
  const getPositionTrackingWindow = (timestamp: string) => {
    const date = parseServerUtcDate(timestamp);
    const from = new Date(date.getTime() - 2 * 60 * 1000);
    const to = new Date(date.getTime() + 2 * 60 * 1000);
    return {
      from: formatDateInServerUtc(from),
      to: formatDateInServerUtc(to),
    };
  };
  let [limit, setLimit] = useState(10);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [list_Notifications, setNotifications] = useState<NotificationsProps[]>(
    []
  );
  const id_user = localStorage.getItem("userid");
  const [showCreateAlarmModal, setShowCreateAlarmModal] = useState(false);
  const handleshowCreateAlarmModal = () => setShowCreateAlarmModal(true);
  const handleCloseCreateAlarmModal = () => setShowCreateAlarmModal(false);
  const [loading, setLoading] = useState(true); // Add loading state
  const [pageCount, setPageCount] = useState(0);
  let [total, setTotal] = useState(0);
  const [column, setSortColum] = useState("0");
  const [sort, setSort] = useState("ASC");
  const [search, setSearch] = useState(id_notification || "");
  const [type, setType] = useState(0);
  const [typeSearch, setTypeSearch] = useState("Notification ID");
  const [notificationType, setNotificationType] = useState("");
  const [dateFrom, setDateFrom] = useState(getTodayDateInputValue);
  const [dateTo, setDateTo] = useState(getTodayDateInputValue);
  const [show, setShow] = useState(false);
  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);
  const handleSubmit = () => {
    // Votre logique de soumission ici
  };

  const latestNotificationRequest = useRef(0);

  const getNotifications = async (
    limitValue: number,
    currentPage: number,
    search: string,
    type: number,
    column: string,
    sort: string,
    notificationType: string,
    dateFrom: string,
    dateTo: string,
    showLoader = true
  ) => {
    const requestId = ++latestNotificationRequest.current;
    try {
      if (showLoader) setLoading(true);
      const options = {
        credentials: "include" as RequestCredentials,
        method: "POST",
        headers: {"Content-Type": "application/json"},
        body: JSON.stringify({limitValue, currentPage, search, type, column, sort, notificationType, dateFrom, dateTo}),
      };
      const [totalResponse, listResponse] = await Promise.all([
        fetch(`${backendUrl}/api/notification/totalpage`, options),
        fetch(`${backendUrl}/api/notification/search`, options),
      ]);
      if (!totalResponse.ok || !listResponse.ok) throw new Error("Notification search failed");
      const [counts, data] = await Promise.all([totalResponse.json(), listResponse.json()]);
      if (requestId !== latestNotificationRequest.current) return;
      const total = Number(counts[0].count);
      setTotal(total);
      setPageCount(Math.ceil(total / limitValue));
      setLimit(limitValue);
      setNotifications(data);
    } catch (error) {
      if (requestId === latestNotificationRequest.current) console.error(error);
    } finally {
      if (requestId === latestNotificationRequest.current) setLoading(false);
    }
  };

  const handlePageClick = async (data: any) => {
    let currentPage = data.selected + 1;
    setCurrentPage(currentPage);
    await getNotifications(
      limit,
      currentPage,
      search,
      type,
      column,
      sort,
      notificationType,
      dateFrom,
      dateTo
    );
    window.scrollTo(0, 0);
  };

  useEffect(() => {
    getNotifications(limit, currentPage, search, type, column, sort, notificationType, dateFrom, dateTo);
    return () => { latestNotificationRequest.current += 1; };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      getNotifications(limit, currentPage, search, type, column, sort, notificationType, dateFrom, dateTo, false);
    }, 30000);

    return () => clearInterval(interval);
  }, [limit, currentPage, search, type, column, sort, notificationType, dateFrom, dateTo]);

  const handleSelectChange = async (event: any) => {
    const newValue = event.target.value;
    setCurrentPage(1);
    setLimit(parseInt(newValue));
    await getNotifications(
      parseInt(newValue),
      1,
      search,
      type,
      column,
      sort,
      notificationType,
      dateFrom,
      dateTo
    );
    window.scrollTo(0, 0);
  };

  const [selectedColumns, setSelectedColumns] = useState({
    id_notification: true,
    id_groupe_alarme: true,
    name_groupe_alarme: true,
    name_alarme: true,
    type: true,
    message: true,
    severity: true,
    timestamp: true,
    infos: true,
    attached: true,
    date_start: true,
    date_end: true,
  });

  const handleColumnChange = (column: string) => {
    setSelectedColumns((prevState: any) => ({
      ...prevState,
      [column]: !prevState[column],
    }));
  };

  const handleTypeSearch = (event: any) => {
    const selectedValue = event.target.textContent;

    let nextType = type;
    switch (selectedValue) {
      case translate("Notification ID"):
        nextType = 0; break;
      case translate("Type"):
        nextType = 1; break;
      case translate("Details"):
        nextType = 2; break;
      case translate("User"):
        nextType = 3; break;
      case translate("Group"):
        nextType = 4; break;
      case translate("Alert"):
        nextType = 5; break;
      case translate("Date"):
        nextType = 6; break;
    }

    setType(nextType);
    setTypeSearch(selectedValue);
    setCurrentPage(1);
    void getNotifications(limit, 1, search, nextType, column, sort, notificationType, dateFrom, dateTo);
    // console.log("Selected value:", selectedValue);
  };

  const handleAdvancedSearch = async (event: any) => {
    const newValue = event.target.value;
    setSearch(newValue);
    setCurrentPage(1);
    await getNotifications(limit, 1, newValue, type, column, sort, notificationType, dateFrom, dateTo);
  };

  const handleResetSearch = async () => {
    setSearch("");
    setType(0);
    setTypeSearch("Notification ID");
    setNotificationType("");
    const today = getTodayDateInputValue();
    setDateFrom(today);
    setDateTo(today);
    setSortColum("0");
    setSort("ASC");
    setLimit(10);
    setCurrentPage(1);
    await getNotifications(10, 1, "", 0, "0", "ASC", "", today, today);
  };

  const handleNotificationFilterChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const nextValue = event.target.value;
    setNotificationType(nextValue);
    setCurrentPage(1);
    void getNotifications(limit, 1, search, type, column, sort, nextValue, dateFrom, dateTo);
  };

  const handleDateFilterChange = (
    setter: React.Dispatch<React.SetStateAction<string>>,
    nextValue: string,
    nextFrom: string,
    nextTo: string
  ) => {
    setter(nextValue);
    setCurrentPage(1);
    void getNotifications(limit, 1, search, type, column, sort, notificationType, nextFrom, nextTo);
  };

  const handleSortingColum = (currentColumn: string) => {
    const nextSort = sort === "ASC" ? "DESC" : "ASC";
    setSortColum(currentColumn);
    setSort(nextSort);
    setCurrentPage(1);
    void getNotifications(limit, 1, search, type, currentColumn, nextSort, notificationType, dateFrom, dateTo);
  };
  const today = getTodayDateInputValue();
  const hasActiveFilters =
    search !== "" ||
    type !== 0 ||
    typeSearch !== "Notification ID" ||
    notificationType !== "" ||
    dateFrom !== today ||
    dateTo !== today ||
    column !== "0" ||
    sort !== "ASC" ||
    limit !== 10 ||
    currentPage !== 1;
  return (
    <div className="notification-page">
      <div className="row">
        <div className="col-md-6 col-sm-12">
          <h4>
            <span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                className="feather feather-bell"
              >
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
              </svg>
            </span>
            {translate("Notifications")} ({total})
          </h4>
        </div>
        <div className="col-md-6 col-sm-12 text-right">
          <NavLink to="/alarms" className="btn btn-outline-info  mr-1">
            <i className="las la-cog mr-3"></i>
            {translate("Configuration Alarme")}
          </NavLink>
        </div>
      </div>
      <div className="row notification-search-row">
        <div className="col-12 notification-toolbar">
          <div className="notification-search-control">
          <ListSearchBar criteria={<Dropdown>
              <Dropdown.Toggle variant="link" id="dropdown-basic">{translate(typeSearch)}</Dropdown.Toggle>
              <Dropdown.Menu onClick={handleTypeSearch}>
                <Dropdown.Item>{translate("Notification ID")}</Dropdown.Item>
                <Dropdown.Item>{translate("Type")}</Dropdown.Item>
                <Dropdown.Item>{translate("Details")}</Dropdown.Item>
                <Dropdown.Item>{translate("User")}</Dropdown.Item>
                <Dropdown.Item>{translate("Group")}</Dropdown.Item>
                <Dropdown.Item>{translate("Alert")}</Dropdown.Item>
                <Dropdown.Item>{translate("Date")}</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>} onReset={handleResetSearch} resetLabel={translate("Reset search")} resetDisabled={!hasActiveFilters}>
<input
              type="text"
              placeholder={`${translate("Search by")} ${translate(typeSearch)}`}
              value={search}
              onChange={handleAdvancedSearch}
              className="form-control"
            />
</ListSearchBar>
          </div>
          <div className="notification-filter-main">
            <div className="notification-filter-control">
            <i className="las la-filter notification-filter-icon" title={translate("Type")} aria-label={translate("Type")} />
            <select
              id="notification-type-filter"
              className="custom-select custom-select-sm"
              value={notificationType}
              onChange={handleNotificationFilterChange}
              aria-label={translate("Type")}
            >
              <option value="">{translate("All")}</option>
              <option value="alarm">{translate("Alarms")}</option>
              <option value="mission">{translate("Mission")}</option>
              <option value="report">{translate("Reports")}</option>
              <option value="security">{translate("Security center")}</option>
              <option value="other">{translate("Other")}</option>
            </select>
          </div>
            <div className="notification-date-controls" title={`${translate("From date")} → ${translate("To date")}`}>
            <i className="las la-calendar-alt notification-filter-icon" aria-hidden="true" />
            <input
              id="notification-date-from"
              type="date"
              className="form-control form-control-sm"
              style={{ width: "190px" }}
              aria-label={translate("From date")}
              value={dateFrom}
              max={dateTo || undefined}
              onChange={(event) => handleDateFilterChange(setDateFrom, event.target.value, event.target.value, dateTo)}
            />
            <span className="notification-date-range-separator" aria-hidden="true">→</span>
            <input
              id="notification-date-to"
              type="date"
              className="form-control form-control-sm"
              style={{ width: "190px" }}
              aria-label={translate("To date")}
              value={dateTo}
              min={dateFrom || undefined}
              onChange={(event) => handleDateFilterChange(setDateTo, event.target.value, dateFrom, event.target.value)}
            />
          </div>
          </div>
          <div className="notification-filter-actions">
          <i className="las la-list-ol notification-filter-icon" title={translate("Show")} aria-label={translate("Show")} />
          <select
            name="DataTables_Table_0_length"
            aria-label={translate("Show")}
            className="custom-select custom-select-sm"
            style={{ width: "66px" }}
            onChange={handleSelectChange}
          >
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="50">50</option>
            <option value="100">100</option>
            <option value="200">200</option>
            <option value="500">500</option>
          </select>
          <Dropdown>
            <Dropdown.Toggle
              variant=""
              id="dropdown-basic"
              title="Colonnes dʼaffichage"
            >
              <i className="las la-eye"></i>
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item
                as="button"
                style={{ display: "flex", alignItems: "center" }}
              >
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={selectedColumns.id_notification}
                  onChange={() => handleColumnChange("id_notification")}
                />
                <span style={{ marginLeft: "10px" }}>
                  {translate("Notification ID")}
                </span>
              </Dropdown.Item>
              <Dropdown.Item
                as="button"
                style={{ display: "flex", alignItems: "center" }}
              >
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={selectedColumns.name_groupe_alarme}
                  onChange={() => handleColumnChange("name_groupe_alarme")}
                />
                <span style={{ marginLeft: "10px" }}>{translate("Group")}</span>
              </Dropdown.Item>
              <Dropdown.Item
                as="button"
                style={{ display: "flex", alignItems: "center" }}
              >
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={selectedColumns.name_alarme}
                  onChange={() => handleColumnChange("name_alarme")}
                />
                <span style={{ marginLeft: "10px" }}>
                  {translate("Alarms")}
                </span>
              </Dropdown.Item>
              <Dropdown.Item
                as="button"
                style={{ display: "flex", alignItems: "center" }}
              >
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={selectedColumns.message}
                  onChange={() => handleColumnChange("message")}
                />
                <span style={{ marginLeft: "10px" }}>
                  {translate("Details")}
                </span>
              </Dropdown.Item>
              <Dropdown.Item
                as="button"
                style={{ display: "flex", alignItems: "center" }}
              >
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={selectedColumns.attached}
                  onChange={() => handleColumnChange("attached")}
                />
                <span style={{ marginLeft: "10px" }}>
                  {translate("Attached to")}
                </span>
              </Dropdown.Item>

              <Dropdown.Item
                as="button"
                style={{ display: "flex", alignItems: "center" }}
              >
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={selectedColumns.date_start}
                  onChange={() => handleColumnChange("date_start")}
                />
                <span style={{ marginLeft: "10px" }}>
                  {translate("Start date")}
                </span>
              </Dropdown.Item>

              <Dropdown.Item
                as="button"
                style={{ display: "flex", alignItems: "center" }}
              >
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={selectedColumns.date_end}
                  onChange={() => handleColumnChange("date_end")}
                />
                <span style={{ marginLeft: "10px" }}>
                  {translate("End date")}
                </span>
              </Dropdown.Item>

              <Dropdown.Item
                as="button"
                style={{ display: "flex", alignItems: "center" }}
              ></Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
          </div>
        </div>
      </div>
      <div className="row m-1">
        <Table className="dataTable">
          <thead className="bg-white text-uppercase">
            <tr className="ligth ligth-data">
              <th>
                <div className="form-check form-check-inline">
                  <input className="form-check-input" type="checkbox" />
                  <label className="form-check-label"></label>
                </div>
              </th>
              {selectedColumns.id_notification && (
                <th
                  className="sorting"
                  onClick={() => handleSortingColum("id_notification")}
                >
                  {translate("Code")}
                </th>
              )}
              {selectedColumns.name_groupe_alarme && (
                <th
                  className="sorting"
                  onClick={() => handleSortingColum("name_groupe_alarme")}
                >
                  {translate("Group")}
                </th>
              )}
              {selectedColumns.name_alarme && (
                <th
                  className="sorting"
                  onClick={() => handleSortingColum("name_alarme")}
                >
                  {translate("Alarmes")}
                </th>
              )}
              {selectedColumns.message && (
                <th
                  className="sorting"
                  onClick={() => handleSortingColum("message")}
                >
                  {translate("Details")}
                </th>
              )}
              {selectedColumns.attached && (
                <th
                  className="sorting"
                  onClick={() => handleSortingColum("attached")}
                >
                  {translate("Attached to")}
                </th>
              )}
              {selectedColumns.date_start && (
                <th
                  className="sorting"
                  onClick={() => handleSortingColum("date_start")}
                >
                  {translate("Start date")}
                </th>
              )}

              {selectedColumns.date_end && (
                <th
                  className="sorting"
                  onClick={() => handleSortingColum("date_end")}
                >
                  {translate("End date")}
                </th>
              )}
              {<th>{translate("Infos")}</th>}
            </tr>
          </thead>
          <tbody key="#" className="ligth-body">
            {loading ? (
              <tr>
                <td className="text-center" colSpan={7}>
                  <p>
                    <PropagateLoader
                      color={"#123abc"}
                      loading={loading}
                      size={20}
                    />
                  </p>
                </td>
              </tr>
            ) : list_Notifications.length > 0 ? (
              list_Notifications.map((notification) => (
                <tr key={notification.id_notification}>
                  <td>
                    <div className="form-check form-check-inline">
                      <input type="checkbox" className="form-check-input" />
                    </div>
                  </td>
                  {selectedColumns.id_notification && (
                    <td>{notification.id_notification}</td>
                  )}
                  {selectedColumns.name_groupe_alarme && (
                    <td>{getNotificationGroupLabel(notification)}</td>
                  )}
                  {selectedColumns.name_alarme && (
                    <td>{getNotificationNameLabel(notification)}</td>
                  )}
                  {selectedColumns.message && (
                    <td title={formatNotificationMessage(notification.message, translate)}>
                      {formatNotificationMessage(notification.message, translate)}
                      {notification.type === 'trip_detected' && (
                        <small className="d-block mt-1">
                          {notification.trip_event_status === 'arrived' ? 'Arrivée détectée le' : 'Retard détecté le'} {toTimestamp(notification.timestamp)}
                        </small>
                      )}
                      {notification.webhook_state && (
                        <div className="mt-2">
                          <span className={`badge badge-${notification.webhook_state === 'delivered' ? 'success' : notification.webhook_state === 'pending' ? 'secondary' : 'warning'}`}>
                            {{ pending: 'Webhook en attente', retry: 'Webhook en échec · réessai prévu', review: 'Webhook à vérifier', delivered: 'Webhook envoyé' }[notification.webhook_state]}
                          </span>
                          <small className="d-block mt-1">
                            {notification.webhook_attempts || 0} tentative(s)
                            {notification.webhook_http_status ? ` · HTTP ${notification.webhook_http_status}` : ''}
                            {notification.webhook_delivered_at ? ` · Envoyé le ${toTimestamp(notification.webhook_delivered_at)}` : ''}
                          </small>
                          {notification.webhook_error && <small className="d-block">{notification.webhook_error}</small>}
                        </div>
                      )}
                    </td>
                  )}
                  {selectedColumns.attached && (
                    <td>{notification.immatriculation_vehicule}</td>
                  )}
                  {selectedColumns.date_start && (
                    <td>{notification.type === 'trip_detected'
                      ? (notification.trip_created_at ? toTimestamp(notification.trip_created_at) : '—')
                      : toTimestamp(notification.timestamp)}</td>
                  )}
                  {selectedColumns.date_end && (
                    <td title={notification.type === 'trip_detected' && notification.trip_event_status !== 'arrived'
                      ? 'Aucune arrivée confirmée par cette notification' : undefined}>
                      {notification.type === 'trip_detected'
                        ? (notification.trip_event_status === 'arrived' ? toTimestamp(notification.timestamp) : '—')
                        : toTimestamp(notification.timestamp)}
                    </td>
                  )}
                  <td>
                    <div className="d-flex align-items-center list-action">
                      {getReportNotificationPath(notification) && (
                        <Link
                          to={getReportNotificationPath(notification) as string}
                          className="badge badge-primary mr-2"
                          data-toggle="tooltip"
                          data-placement="top"
                          title={translate("Open report")}
                        >
                          <i className="las la-file-alt" style={{ fontSize: "1.2em" }}></i>
                        </Link>
                      )}
                      <Link
                        to={(() => {
                          const range = getPositionTrackingWindow(notification.timestamp);
                          return `/position-tracking/${range.from}/${range.to}/${notification.immatriculation_vehicule}`;
                        })()}
                        className="badge badge-success mr-2"
                        data-toggle="tooltip"
                        data-placement="top"
                        title={translate("Details")}
                      >
                        <i
                          className="las la-bell"
                          style={{ fontSize: "1.2em" }}
                        ></i>
                      </Link>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={9}>{translate("No notification available")}</td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
      <div className="row">
        <div className="col-md-6 d-flex align-items-center">
          <span>
            {translate("Displaying")} {list_Notifications.length}{" "}
            {translate("out of")} {total}
          </span>
        </div>
        <div className="col-md-6">
          <ReactPaginate
            previousLabel={translate("previous")}
            nextLabel={translate("next")}
            breakLabel={"..."}
            pageCount={pageCount}
            forcePage={pageCount > 0 ? Math.min(currentPage - 1, pageCount - 1) : 0}
            marginPagesDisplayed={2}
            pageRangeDisplayed={3}
            onPageChange={handlePageClick}
            containerClassName={"pagination justify-content-center"}
            pageClassName={"page-item"}
            pageLinkClassName={"page-link"}
            previousClassName={"page-item"}
            previousLinkClassName={"page-link"}
            nextClassName={"page-item"}
            nextLinkClassName={"page-link"}
            breakClassName={"page-item"}
            breakLinkClassName={"page-link"}
            activeClassName={"active"}
          />
        </div>
      </div>
    </div>
  );
}
